// pages/PropertyDetail.js
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import config from '../config';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PropertyDetail.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const STAGES = [
  { key: 'Request Received',  label: 'Request\nReceived' },
  { key: 'Processing',        label: 'Processing' },
  { key: 'Submitted to City', label: 'Submitted\nto City' },
  { key: 'Result',            label: 'Result' },
];

function normalise(status) {
  return (status || '').trim().toLowerCase();
}

function isPassed(status)    { return normalise(status) === 'passed'; }
function isCompleted(status) { return normalise(status) === 'completed'; }
function isFailed(status)    { return normalise(status) === 'failed'; }
function isCancelled(status) { return normalise(status) === 'cancelled'; }
function isResult(status)    { return isPassed(status) || isCompleted(status) || isFailed(status); }

// ─── Timeline ─────────────────────────────────────────────────────────────────
function Timeline({ currentStatus }) {
  if (isCancelled(currentStatus)) {
    return (
      <div className="tl-wrapper">
        <div className="tl-cancelled-node">
          <div className="tl-circle tl-cancelled-circle">
            <span>✕</span>
          </div>
          <div className="tl-label tl-cancelled-label">Request Cancelled</div>
        </div>
      </div>
    );
  }

  const norm = normalise(currentStatus);
  const stageKeyMap = {
    'request received':  0,
    'processing':        1,
    'with contractor':   1,
    'submitted to city': 2,
    'passed':            3,
    'completed':         3,
    'failed':            3,
  };
  const currentIndex = stageKeyMap[norm] ?? -1;

  const getState = (stageKey, index) => {
    const sk = stageKey.toLowerCase();
    if (sk === 'result') {
      if (isPassed(currentStatus))    return 'pass';
      if (isCompleted(currentStatus)) return 'pass';
      if (isFailed(currentStatus))    return 'fail';
      return 'pending';
    }
    if (index < currentIndex)  return 'completed';
    if (index === currentIndex) return 'current';
    return 'pending';
  };

  return (
    <div className="tl-wrapper">
      <div className="tl-track">
        {STAGES.map((stage, i) => {
          const state     = getState(stage.key, i);
          const isLast    = i === STAGES.length - 1;
          const nextState = !isLast ? getState(STAGES[i + 1].key, i + 1) : null;
          const connDone  = state === 'completed' &&
            (nextState === 'completed' || nextState === 'current' ||
             nextState === 'pass'      || nextState === 'fail');

          const displayLabel =
            stage.key === 'Result' && isResult(currentStatus)
              ? (isFailed(currentStatus) ? 'Failed' : isCompleted(currentStatus) ? 'Completed' : 'Passed')
              : stage.key === 'Processing' && norm === 'with contractor'
                ? 'With\nContractor'
                : stage.label;

          return (
            <React.Fragment key={stage.key}>
              <div className={`tl-node tl-${state}`}>
                <div className="tl-circle">
                  {state === 'completed' && <span>✓</span>}
                  {state === 'pass'      && <span>✓</span>}
                  {state === 'fail'      && <span>✕</span>}
                  {state === 'current'   && <span>{currentIndex + 1}</span>}
                </div>
                                <div className="tl-label">
                  {displayLabel.split('\n').map((line, j) => (
                    <span key={j}>{line}<br /></span>
                  ))}
                </div>
              </div>
              {!isLast && (
                <div className={`tl-connector ${connDone ? 'tl-conn-done' : 'tl-conn-pending'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── PDF Viewer Modal ─────────────────────────────────────────────────────────
function PdfViewerModal({ url, onClose }) {
  const [numPages, setNumPages]   = useState(null);
  const [pageWidth, setPageWidth] = useState(800);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const updateWidth = () => {
      const modal = document.querySelector('.pd-pdf-modal');
      if (modal) setPageWidth(modal.clientWidth - 48);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  return (
    <div className="pd-pdf-overlay" onClick={onClose}>
      <div className="pd-pdf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pd-pdf-header">
          <span className="pd-pdf-title">Deficiency Report</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: '0.8rem', color: '#6b7280', textDecoration: 'underline' }}
            >
              Open in new tab
            </a>
            <button className="pd-pdf-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="pd-pdf-body">
          <Document
            file={url}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={<div className="pd-pdf-loading">Loading PDF…</div>}
            error={<div className="pd-pdf-error">Failed to load PDF. <a href={url} target="_blank" rel="noreferrer">Open in new tab</a></div>}
          >
            {Array.from({ length: numPages || 0 }, (_, i) => (
              <Page
                key={i + 1}
                pageNumber={i + 1}
                width={pageWidth}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PropertyDetail() {
  const [property, setProperty]         = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [imgLoaded, setImgLoaded]       = useState(false);
  const [pdfOpen, setPdfOpen]           = useState(false);
  const navigate = useNavigate();
  const { id }   = useParams();

  const fetchData = useCallback(() => {
    const code = sessionStorage.getItem('customerCode');
    const storedCustomer = sessionStorage.getItem('customerData');
    if (!code && !storedCustomer) { navigate('/'); return; }

    if (code) {
      axios.get(`${config.apiUrl}/api/customer/${code}`)
        .then((res) => {
          const fresh = res.data;
          sessionStorage.setItem('customerData', JSON.stringify(fresh));
          setCustomerData(fresh);
          const prop = fresh.properties.find((p) => String(p.id) === String(id));
          if (prop) {
            sessionStorage.setItem('selectedProperty', JSON.stringify(prop));
            setProperty(prop);
          } else {
            navigate('/dashboard');
          }
        })
        .catch(() => {
          // fall back to session cache
          if (storedCustomer) {
            try {
              const cached = JSON.parse(storedCustomer);
              setCustomerData(cached);
              const prop = cached.properties.find((p) => String(p.id) === String(id));
              if (prop) setProperty(prop);
              else navigate('/dashboard');
            } catch { navigate('/'); }
          } else { navigate('/'); }
        });
    } else {
      try {
        const cached = JSON.parse(storedCustomer);
        setCustomerData(cached);
        const prop = cached.properties.find((p) => String(p.id) === String(id));
        if (prop) setProperty(prop);
        else navigate('/dashboard');
      } catch { navigate('/'); }
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!property || !customerData) return null;

  const passed    = isPassed(property.current_status);
  const completed = isCompleted(property.current_status);
  const cancelled = isCancelled(property.current_status);

  const badgeCls = passed || completed            ? 'badge-pass'
    : isFailed(property.current_status) ? 'badge-fail'
    : cancelled                         ? 'badge-cancelled'
    :                                     'badge-progress';

  return (
    <div className="pd-root">
      {/* Nav */}
      <nav className="pd-nav">
        <div className="pd-nav-left">
          <button className="pd-back-btn" onClick={() => navigate('/dashboard')}>← Back</button>
          <a href="https://vncosurveys.com" target="_blank" rel="noreferrer">
            <img src="/images/logo.png" alt="VNCO SURVEYS" className="navbar-logo" />
          </a>
        </div>
        <div className="pd-nav-right">
          <button className="pd-refresh-btn" onClick={fetchData}>↺ Refresh</button>
          <span className="pd-nav-client">
            {customerData.customer?.company_name || customerData.company_name}
          </span>
        </div>
      </nav>

      {/* Hero */}
      <div className="pd-hero">
        <div className="pd-hero-inner">
          <div className="pd-hero-meta">
            <span className="pd-service-tag">{property.service_type}</span>
          </div>
          <h1 className="pd-address">{property.address}</h1>
          <span className={`pd-status-badge ${badgeCls}`}>{property.current_status}</span>
        </div>
      </div>

      {/* Content */}
      <div className="pd-content">
        <div className="pd-card">
          <p className="pd-card-title">Certification progress</p>
          <Timeline currentStatus={property.current_status} />
        </div>

        {/* Deficiency photo */}
        {property.deficiency_photo_url && !passed && !completed && !cancelled && (
          <div className="pd-card pd-deficiency-card">
            <div className="pd-deficiency-header">
              <p className="pd-card-title">Deficiency recorded</p>
            </div>
            <div className="pd-photo-wrap">
              {!imgLoaded && <div className="pd-skeleton">Loading photo…</div>}
              <img
                src={property.deficiency_photo_url}
                alt="Deficiency"
                className={`pd-photo ${imgLoaded ? 'img-show' : 'img-hide'}`}
                onLoad={() => setImgLoaded(true)}
                onError={() => setImgLoaded(true)}
              />
            </div>
          </div>
        )}

        {/* Deficiency PDF */}
        {property.deficiency_pdf_url && !passed && !completed && !cancelled && (
          <div className="pd-card pd-deficiency-card">
            <div className="pd-deficiency-header">
              <p className="pd-card-title">Deficiency report</p>
            </div>
            <button className="pd-pdf-btn" onClick={() => setPdfOpen(true)}>
              View PDF report
            </button>
          </div>
        )}

        <div className="pd-card">
          <p className="pd-card-title">Property details</p>
          <dl className="pd-details-grid">
            <div className="pd-detail-item">
              <dt className="pd-detail-label">Service Type</dt>
              <dd className="pd-detail-value">{property.service_type}</dd>
            </div>
            <div className="pd-detail-item">
              <dt className="pd-detail-label">Submission Date</dt>
              <dd className="pd-detail-value">{property.submission_date || '—'}</dd>
            </div>
            <div className="pd-detail-item">
              <dt className="pd-detail-label">Job Number</dt>
              <dd className="pd-detail-value">{property.job_number || '—'}</dd>
            </div>
            <div className="pd-detail-item">
              <dt className="pd-detail-label">Municipality</dt>
              <dd className="pd-detail-value">{property.municipality || '—'}</dd>
            </div>
          </dl>
        </div>
      </div>

      {pdfOpen && property.deficiency_pdf_url && (
        <PdfViewerModal url={property.deficiency_pdf_url} onClose={() => setPdfOpen(false)} />
      )}
    </div>
  );
}